import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface ActionButton {
  label: string;
  icon?: React.ElementType;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
}

interface StandardHeaderProps {
  title: string;
  subtitle: string;
  breadcrumb?: { label: string; onClick?: () => void }[];
  actions?: ActionButton[];
  children?: React.ReactNode;
}

export default function StandardHeader({ title, subtitle, breadcrumb, actions, children }: StandardHeaderProps) {
  const { activeWorkspace } = useAuth();

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative text-left pt-2 pb-1">
      <div className="flex flex-col gap-2">
        {/* Breadcrumb - More subtle */}
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          <span className="hover:text-[#0F172A] cursor-pointer transition-colors">Workspace</span>
          <ChevronRight size={10} className="opacity-40" />
          {breadcrumb?.map((item, index) => (
            <React.Fragment key={index}>
              <span 
                className={`${item.onClick ? 'hover:text-[#0F172A] cursor-pointer' : 'text-slate-500'} transition-colors`}
                onClick={item.onClick}
              >
                {item.label}
              </span>
              {index < breadcrumb.length - 1 && <ChevronRight size={10} className="opacity-40" />}
            </React.Fragment>
          )) || <span className="text-[#0F172A]">{title}</span>}
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-display font-black text-[#0F172A] tracking-tight leading-tight">
              {title}
            </h1>
            {activeWorkspace && (
              <div className="px-2.5 py-0.5 bg-slate-100/80 border border-slate-200/80 rounded-lg flex items-center gap-1.5 shadow-2xs">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.4)]" />
                <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">
                  {activeWorkspace.name}
                </span>
              </div>
            )}
          </div>
          <p className="text-xs sm:text-[13px] text-slate-500 font-medium max-w-2xl leading-relaxed mt-0.5">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
        {actions?.map((action, i) => {
          const isPrimary = action.variant === 'primary' || !action.variant;
          const isGhost = action.variant === 'ghost';
          
          if (isPrimary) {
            return (
              <button 
                key={i}
                onClick={action.onClick}
                disabled={action.disabled}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#0F172A] text-white font-bold text-xs tracking-wide hover:bg-slate-800 shadow-2xs hover:shadow-xs active:scale-[0.99] transition-all cursor-pointer disabled:opacity-50"
              >
                {action.icon && <action.icon size={14} strokeWidth={2.2} />}
                <span>{action.label}</span>
              </button>
            );
          }

          if (isGhost) {
            return (
              <button 
                key={i}
                onClick={action.onClick}
                disabled={action.disabled}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-slate-600 hover:text-[#0F172A] hover:bg-slate-100/80 font-bold text-xs tracking-wide transition-all cursor-pointer disabled:opacity-50"
              >
                {action.icon && <action.icon size={14} strokeWidth={2} />}
                <span>{action.label}</span>
              </button>
            );
          }

          return (
            <button 
              key={i}
              onClick={action.onClick}
              disabled={action.disabled}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-[#0F172A15] text-[#0F172A] font-bold text-xs tracking-wide hover:bg-slate-50 hover:border-[#0F172A25] shadow-2xs transition-all cursor-pointer disabled:opacity-50"
            >
              {action.icon && <action.icon size={14} strokeWidth={2} className="text-slate-500" />}
              <span>{action.label}</span>
            </button>
          );
        })}
        {children}
      </div>
    </div>
  );
}
